/**
 * The relink subsystem's shared state (M3 plan R19): the live configs, the current system's relay slots (repair
 * ports, objective points and repair lists, rebuilt on a system change or a dev-panel edit), the running reboot and
 * the prompt texts, plus the allocation-free readers that the step, the prompts and the commands share (D-014).
 */
import { rotate } from '../galaxy/layout/frame'
import type { FactionsConfig } from '../galaxy/layout/config'
import type { RelaySite, SystemLayout, Vec3T } from '../galaxy/layout/types'
import { slowBelowText } from '../reach/interaction/hail'
import type { ReachCore } from '../reach/runtime/types'
import type { SessionConfig } from '../reach/session/config'
import type { StandInConfig } from '../reach/standin'
import { PART_IDS, type PartId, type RelayStatus } from '../reach/types'
import type { Sim } from '../sim'
import { Life, Pos64, Vel } from '../world/components'
import type { RelinkConfig } from './config'
import { partsText, repairList, type ScheduledSpawn } from './rules'

/** The relay model's repair-port socket (asset contract; plan §2.8). */
export const REPAIR_SOCKET = 'DOCK_REPAIR'

/** The current system's relays by slot (the layout's `relays` order). */
export interface RelaySlots {
  /** The layout these slots describe (null: none yet). */
  layout: SystemLayout | null
  /** The context `version` they were built for. */
  version: number
  sites: readonly RelaySite[]
  slotOf: Map<string, number>
  /** The repair port (world), 3 per slot. */
  port: Float64Array
  /** The siege objective's point: the port moved `objectiveInsetM` toward the ring centre, 3 per slot. */
  objective: Float64Array
  /** The repair list for this system's region, 3 per slot in `PART_IDS` order. */
  required: Int32Array
}

/** The reboot in progress (the rest lives in `core.relink`). */
export interface RebootRun {
  /** The relay's slot, −1 when idle. */
  slot: number
  relayId: string
  schedule: ScheduledSpawn[]
  /** The next schedule entry to spawn. */
  next: number
  /** Collider handles remapped to the objective. */
  handles: number[]
  /** `session.structuresVersion` at the last remap (a re-stream means remapping again). */
  structuresVersion: number
  /** The spawned guardian group (M5 C14: on the guardian team), −1 = none this reboot. */
  guardianGroup: number
}

/** Prompt texts, built once per config version (never per tick). */
export interface RelinkTexts {
  version: number
  slow: string
  noParts: string
  busy: string
  laneNeeds: string
}

export interface RelinkContext {
  /** Live: the dev panel edits it in place, then bumps `version`. */
  readonly cfg: RelinkConfig
  readonly factions: FactionsConfig
  /** The stand-in's prices and part masses (the core's live config when it has one). */
  standIn: StandInConfig
  /** The session config (hail range for buying parts; the core's live config when it has one). */
  session: SessionConfig
  version: number
  readonly slots: RelaySlots
  readonly run: RebootRun
  readonly text: RelinkTexts
}

export function createRelinkContext(cfg: RelinkConfig, factions: FactionsConfig, standIn: StandInConfig, session: SessionConfig): RelinkContext {
  return {
    cfg,
    factions,
    standIn,
    session,
    version: 1,
    slots: {
      layout: null,
      version: 0,
      sites: [],
      slotOf: new Map(),
      port: new Float64Array(0),
      objective: new Float64Array(0),
      required: new Int32Array(0),
    },
    run: { slot: -1, relayId: '', schedule: [], next: 0, handles: [], structuresVersion: -1, guardianGroup: -1 },
    text: { version: 0, slow: '', noParts: 'No matching parts in the hold', busy: 'Another relay reboot is running', laneNeeds: '' },
  }
}

/** The repair port of `site` (its pose composed with the model's repair socket; the centre without one). */
export function repairPort(site: RelaySite, core: ReachCore): Vec3T {
  const socket = core.colliders.socketsOf(site.assetId).find((s) => s.name === REPAIR_SOCKET)
  if (socket === undefined) return site.position
  const o = rotate(site.rotation, socket.position)
  return [site.position[0] + o[0], site.position[1] + o[1], site.position[2] + o[2]]
}

/** Rebuilds the slots for `layout` (a rare event: a system change or a dev-panel edit). */
function buildSlots(ctx: RelinkContext, core: ReachCore, layout: SystemLayout | null): void {
  const s = ctx.slots
  const sites = layout?.relays ?? []
  s.layout = layout
  s.version = ctx.version
  s.sites = sites
  s.slotOf = new Map(sites.map((r, i) => [r.relayId, i]))
  s.port = new Float64Array(sites.length * 3)
  s.objective = new Float64Array(sites.length * 3)
  s.required = new Int32Array(sites.length * 3)
  const inset = ctx.cfg.reboot.objectiveInsetM
  for (let i = 0; i < sites.length; i++) {
    const site = sites[i]
    const port = repairPort(site, core)
    const dx = site.position[0] - port[0]
    const dy = site.position[1] - port[1]
    const dz = site.position[2] - port[2]
    const d = Math.hypot(dx, dy, dz)
    const k = d > 0 ? Math.min(inset, d) / d : 0
    for (let a = 0; a < 3; a++) s.port[i * 3 + a] = port[a]
    s.objective[i * 3] = port[0] + dx * k
    s.objective[i * 3 + 1] = port[1] + dy * k
    s.objective[i * 3 + 2] = port[2] + dz * k
    if (layout !== null) {
      const list = repairList(site.long, layout.region, ctx.cfg)
      for (let p = 0; p < PART_IDS.length; p++) s.required[i * 3 + p] = list[PART_IDS[p]]
    }
  }
}

/** The slots of the session's current layout, rebuilt when the system or the config changed. Idle: allocation-free. */
export function ensureSlots(ctx: RelinkContext, core: ReachCore): RelaySlots {
  const layout = core.session.layout
  if (ctx.slots.layout !== layout || ctx.slots.version !== ctx.version) buildSlots(ctx, core, layout)
  return ctx.slots
}

/** The prompt texts for the live config, rebuilt when it changed. Idle: allocation-free. */
export function texts(ctx: RelinkContext): RelinkTexts {
  const t = ctx.text
  if (t.version !== ctx.version) {
    t.version = ctx.version
    t.slow = slowBelowText(ctx.cfg.portMaxSpeed)
    t.laneNeeds = `Needs ${partsText(ctx.cfg.laneRepair.parts)}`
  }
  return t
}

/** The relay's status (the generator's online flag when nothing is stored). Allocation-free. */
export function relayStatus(core: ReachCore, site: RelaySite): RelayStatus {
  const rs = core.state.relays[site.relayId]
  if (rs !== undefined) return rs.status
  return core.galaxy.relays[site.index]?.online === true ? 'online' : 'offline'
}

/** Whether the relay's repair list is known. Allocation-free. */
export function isScanned(core: ReachCore, relayId: string): boolean {
  return core.state.relays[relayId]?.scanned === true
}

/** Parts of `part` delivered to the relay. Allocation-free. */
export function deliveredOf(core: ReachCore, relayId: string, part: PartId): number {
  return core.state.relays[relayId]?.delivered[part] ?? 0
}

/** Parts still missing at `slot`, all parts together. Allocation-free. */
export function missingTotal(core: ReachCore, slots: RelaySlots, slot: number): number {
  const id = slots.sites[slot].relayId
  let n = 0
  for (let p = 0; p < PART_IDS.length; p++) {
    const m = slots.required[slot * 3 + p] - deliveredOf(core, id, PART_IDS[p])
    if (m > 0) n += m
  }
  return n
}

/** True when the hold carries any part still missing at `slot`. Allocation-free. */
export function carriesMissing(core: ReachCore, slots: RelaySlots, slot: number): boolean {
  const id = slots.sites[slot].relayId
  for (let p = 0; p < PART_IDS.length; p++) {
    const part = PART_IDS[p]
    if (slots.required[slot * 3 + p] > deliveredOf(core, id, part) && core.career.cargo(part) > 0) return true
  }
  return false
}

/** The living player's entity while the system is ready, else −1. Allocation-free. */
export function livePlayer(core: ReachCore, sim: Sim): number {
  const p = sim.playerEid
  return p >= 0 && Life.alive[p] === 1 && core.session.ready && core.session.layout !== null ? p : -1
}

/**
 * Squared distance from entity `e` to the world point (x, y, z). For commands: per-tick code reads positions in
 * place, because float arguments to a call that is not inlined are boxed on every call.
 */
export function dist2(e: number, x: number, y: number, z: number): number {
  const dx = Pos64.x[e] - x
  const dy = Pos64.y[e] - y
  const dz = Pos64.z[e] - z
  return dx * dx + dy * dy + dz * dz
}

/** A core that can save (the headless core and the browser runtime); bare test cores cannot. */
export function persistable(core: ReachCore): core is ReachCore & { persist(reason: 'auto' | 'manual'): boolean } {
  return typeof (core as { persist?: unknown }).persist === 'function'
}

/** Entity `e`'s speed (m/s). For commands (see `dist2`). */
export function speedOf(e: number): number {
  return Math.sqrt(Vel.x[e] * Vel.x[e] + Vel.y[e] * Vel.y[e] + Vel.z[e] * Vel.z[e])
}
